/* eslint-disable @typescript-eslint/no-empty-function */
import { FC, useEffect, useState, useMemo, useCallback } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import styles from './DeactivateProductsDrawer.module.css';
import { DeactivateProductsDrawerProps } from './DeactivateProductsDrawer.types';
import { t } from 'i18next';
import { useRemoveProduct } from '@/services/menu/menus.service';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import { EditDrawerProcessing } from '../../SharedComponents/EditDrawerProcessing';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { PriceUpdateAPIResponse } from '@/types/stores.types';
import { useSTKContext } from '@/context/context';
import { getStores } from '@/services/menu/menus.helper.service';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';

export const DeactivateProductsDrawer: FC<DeactivateProductsDrawerProps> = ({
  isOpen,
  onClose,
  storeProducts,
  afterSave,
  setPriceDeviationsData,
}) => {
  const [state, setState] = useState(isOpen);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivationComplete, setDeactivationComplete] = useState(false);
  const [deactivationResponse, setDeactivationResponse] = useState<PriceUpdateAPIResponse | undefined>();
  const { storesAndGroups } = useSTKContext();

  const { mutate: removeProduct } = useRemoveProduct();

  // Get all store group names from the user's selection (same as PriceChangeConfirmation)
  const allStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }

    // Extract store group names based on origin
    if (storesAndGroups.origin === 'tab-store') {
      // When selecting individual stores, get unique store group names
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet).sort();
    }

    if (storesAndGroups.origin === 'tab-stores-groups') {
      // When selecting store groups, get the group names
      return (storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || []).sort();
    }

    return [];
  }, [storesAndGroups]);

  // Get store counts from context (same as PriceChangeConfirmation)
  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;

      if (!storesAndGroups) {
        return { count: 0, total: 0 };
      }

      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count || 0;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length || 0;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount || 0;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length || 0;
      }

      return { count: count || 0, total: total || 0 };
    },
    [storesAndGroups],
  );

  useEffect(() => {
    setState(isOpen);
    if (!isOpen) {
      setIsDeactivating(false);
      setDeactivationComplete(false);
      setDeactivationResponse(undefined);
    }
  }, [isOpen]);

  // Build request payload from selected products
  // Only use stores that are selected in the context, not all stores from variantStores
  const requestPayload = useMemo(() => {
    const selectedStores = getStores(storesAndGroups);
    const variantIds = new Set<string>();

    storeProducts.forEach((product) => {
      // Extract variantId
      if (product.variantId) {
        variantIds.add(product.variantId);
      }
    });

    return {
      stores: selectedStores,
      variantIds: Array.from(variantIds),
    };
  }, [storeProducts, storesAndGroups]);

  // Calculate unique store count for processing state
  const uniqueStoreCount = useMemo(() => {
    return requestPayload.stores.length;
  }, [requestPayload]);

  const handleConfirmDeactivation = () => {
    setIsDeactivating(true);
    removeProduct(requestPayload, {
      onSuccess: (response) => {
        setDeactivationResponse(response as PriceUpdateAPIResponse);
        setDeactivationComplete(true);
        setIsDeactivating(false);
        // Call afterSave immediately to refresh data, just like EditPriceDrawer does
        afterSave();
      },
      onError: (error: any) => {
        // Handle 504 Gateway Timeout errors - request is still processing on backend
        if (
          error?.response?.status === 504 ||
          error?.code === 'ECONNABORTED' ||
          error?.message?.includes('timeout') ||
          error?.message?.includes('Gateway Timeout')
        ) {
          // Create a timeout response object that EditDrawerSuccessAndFailure can handle
          const timeoutResponse = {
            failedStores: [],
            status: 'TIMEOUT',
            message: t('requestProcessingModal.timeout.message'),
          } as unknown as PriceUpdateAPIResponse;
          setDeactivationResponse(timeoutResponse);
          setDeactivationComplete(true);
          setIsDeactivating(false);
          // Call afterSave to refresh data and clear selections even on timeout
          afterSave();
          return;
        }
        // Handle other errors (exceptions, backend errors, no response)
        // Create an error response object that EditDrawerSuccessAndFailure can handle
        const errorResponse = {
          // successStoresCount is missing/undefined - this triggers the allMenusFailedTitle display
          failedStores: [],
          status: 'ERROR',
          message: error?.message || t('deactivateProducts.errorMessage'),
        } as unknown as PriceUpdateAPIResponse;
        setDeactivationResponse(errorResponse);
        setDeactivationComplete(true);
        setIsDeactivating(false);
        // Call afterSave to refresh data and clear selections even on error
        afterSave();
      },
    });
  };

  const onCloseDrawer = () => {
    // Reset all states when closing the drawer
    setDeactivationComplete(false);
    setIsDeactivating(false);
    setDeactivationResponse(undefined);
    setState(false);
    onClose();
  };

  return (
    <div>
      <SwipeableDrawer open={state} onClose={onCloseDrawer} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('deactivateProducts.title')}</h1>
            </div>
            <button type="button" onClick={onCloseDrawer}>
              <X data-testid="x-icon" size={24} color="var(--black-900)" />
            </button>
          </header>
          {(isDeactivating || (deactivationComplete && deactivationResponse)) && (
            <div className={styles.alertBanner}>
              <CalendarAlert />
            </div>
          )}

          <div className={styles.content}>
            {isDeactivating ? (
              <EditDrawerProcessing count={uniqueStoreCount} entityToUpdate={EntityToUpdate.MENU} />
            ) : deactivationComplete && deactivationResponse ? (
              <div className={styles.successFailureWrapper}>
                <EditDrawerSuccessAndFailure
                  apiResponse={deactivationResponse as PriceUpdateAPIResponse}
                  entityToUpdate={EntityToUpdate.MENU}
                />
              </div>
            ) : (
              <>
                <div className={styles.confirmationContent}>
                  <h2 className={styles.confirmationTitle}>{t('deactivateProducts.confirmDeactivation')}</h2>
                  <p className={styles.deactivationInfo}>{t('deactivateProducts.deactivationInfoText')}</p>

                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th className={styles.productHeader}>{t('editMenuPrice.product')}</th>
                          {allStoreGroupNames.map((storeGroupName) => {
                            const { count, total } = getStoreGroupsCount(storeGroupName);
                            return (
                              <th key={storeGroupName} className={styles.priceHeader}>
                                <div className={styles.headerWithCount}>
                                  <div className={styles.headerName}>{storeGroupName}</div>
                                  <div className={styles.headerCount}>
                                    {count} / {total}
                                  </div>
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {storeProducts.map((product, index) => (
                          <tr key={product.variantId || index}>
                            <td className={styles.productCell}>
                              <div className={styles.productName}>{product.productName}</div>
                              <div className={styles.productDescription}>{product.productDescription}</div>
                            </td>
                            {allStoreGroupNames.map((storeGroupName) => {
                              // Show "Inactive" for all selected store groups to indicate deactivation
                              return (
                                <td key={storeGroupName} className={styles.priceCell}>
                                  {t('common.inactive')}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className={styles.footer}>
                  <div className={styles.alertContainer}>
                    <CalendarAlert />
                  </div>

                  <div className={styles.buttonContainer}>
                    <Button
                      className={styles.cancelButton}
                      onClick={onCloseDrawer}
                      variant={ButtonVariants.Secondary}
                      type="button"
                      disabled={isDeactivating}
                    >
                      {t('editMenuPrice.cancel')}
                    </Button>
                    <Button
                      className={styles.submitButton}
                      onClick={handleConfirmDeactivation}
                      variant={ButtonVariants.Primary}
                      type="button"
                      isLoading={isDeactivating}
                      disabled={isDeactivating}
                    >
                      {t('deactivateProducts.confirmDeactivationButton')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
    </div>
  );
};
